// hooks/useFeedPageParams.js - UPDATED with buttonName support
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Enhanced table configurations with validation and display metadata
const TABLE_CONFIGS = {
  // Legislative tables
  congressional_bills: { 
    defaultOrderBy: 'action_date',
    allowedOrderBy: ['action_date', 'created_at', 'title'],
    hasAgencyFilter: false,
    buttonName: 'Bill',
    feedName: 'Bills',
    branch: 'Legislative',
    topic: 'N/A'
  },
  congressional_bills_enrolled: { 
    defaultOrderBy: 'date_issued',
    allowedOrderBy: ['date_issued', 'created_at', 'bill_number'],
    hasAgencyFilter: false,
    buttonName: 'Bill',
    feedName: 'Bills Enrolled',
    branch: 'Legislative',
    topic: 'N/A'
  },
  public_and_private_laws: { 
    defaultOrderBy: 'date_issued',
    allowedOrderBy: ['date_issued', 'law_number', 'created_at'],
    hasAgencyFilter: false,
    buttonName: 'Law',
    feedName: 'Laws',
    branch: 'Legislative',
    topic: 'N/A'
  },
  congressional_reports: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at', 'title'],
    hasAgencyFilter: false,
    buttonName: 'Report',
    feedName: 'Congressional Reports',
    branch: 'Legislative',
    topic: 'N/A'
  },
  congressional_hearings: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at', 'title'],
    hasAgencyFilter: false,
    buttonName: 'Hearing',
    feedName: 'Congressional Hearings',
    branch: 'Legislative',
    topic: 'N/A'
  },
  government_accountability_office_reports: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at', 'title'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'GAO Reports',
    branch: 'Legislative',
    topic: 'N/A'
  },
  committee_materials: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at', 'title'],
    hasAgencyFilter: false,
    usesCommitteeAPI: true,
    buttonName: 'Bill',
    feedName: 'Committee Materials',
    branch: 'Legislative',
    topic: 'N/A'
  },
  committee_meetings: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at', 'meeting_date', 'title'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Committee Meetings',
    branch: 'Legislative',
    topic: 'N/A'
  },
  house_floor_activity: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'House Floor Activity',
    branch: 'Legislative',
    topic: 'N/A'
  },
  senate_committee_news: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'News',
    feedName: 'Senate Committee News',
    branch: 'Legislative',
    topic: 'N/A'
  },

  // Executive tables
  the_white_house: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'White House',
    branch: 'Executive',
    topic: 'N/A'
  },
  compilation_of_presidential_documents: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Doc',
    feedName: 'Presidential Documents',
    branch: 'Executive',
    topic: 'N/A'
  },
  presidential_proclamation: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Presidential Proclamations',
    branch: 'Executive',
    topic: 'N/A'
  },

  // Judicial tables
  uscourts: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'U.S. Court News',
    branch: 'Judicial',
    topic: 'N/A'
  },
  supreme_court_opinions: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Supreme Court Opinions',
    branch: 'Judicial',
    topic: 'N/A'
  },
  supreme_court_arguments: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Supreme Court Arguments',
    branch: 'Judicial',
    topic: 'N/A'
  },
  court_of_appeals: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Court of Appeals',
    branch: 'Judicial',
    topic: 'N/A'
  },
  state_judicial_publications: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'District Court',
    branch: 'Judicial',
    topic: 'N/A'
  },

  // Department tables
  state: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'Department of State',
    branch: 'Executive',
    topic: 'N/A'
  },
  treasury: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'Department of Treasury',
    branch: 'Executive',
    topic: 'Economy'
  },
  defense: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'Department of Defense',
    branch: 'Executive',
    topic: 'Foreign Affairs'
  },
  justice: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Department of Justice',
    branch: 'Executive',
    topic: 'N/A'
  },
  transportation: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'Department of Transportation',
    branch: 'Executive',
    topic: 'N/A'
  },
  interior: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'Department of the Interior',
    branch: 'Executive',
    topic: 'N/A'
  },
  department_of_interior_news: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Department of the Interior News',
    branch: 'Executive',
    topic: 'N/A'
  },
  environment: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'Department of Agriculture',
    branch: 'Executive',
    topic: 'Environment'
  },
  commerce: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'Department of Commerce',
    branch: 'Executive',
    topic: 'Economy'
  },
  labor: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'Department of Labor',
    branch: 'Executive',
    topic: 'Economy'
  },
  health_and_human_services: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Health and Human Services',
    branch: 'Executive',
    topic: 'Health'
  },
  housing_and_urban_development: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'Housing and Urban Development',
    branch: 'Executive',
    topic: 'Environment'
  },
  education: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'Department of Education',
    branch: 'Executive',
    topic: 'N/A'
  },
  energy: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'Department of Energy',
    branch: 'Executive',
    topic: 'Environment'
  },
  homeland_security: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'Department of Homeland Security',
    branch: 'Executive',
    topic: 'N/A'
  },

  // Topic/Agency specific tables
  epa: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'EPA',
    branch: 'N/A',
    topic: 'Environment'
  },
  nasa: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'NASA',
    branch: 'Executive',
    topic: 'Science & Tech'
  },
  nasa_jpl: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'NASA JPL News',
    branch: 'Executive',
    topic: 'Science & Tech'
  },
  nist: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'NIST News',
    branch: 'N/A',
    topic: 'Science & Tech'
  },
  national_science_foundation: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: '',
    feedName: 'National Science Foundation',
    branch: 'Executive',
    topic: 'Science & Tech'
  },
  national_weather_service_update: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'National Weather Service Update',
    branch: 'N/A',
    topic: 'Environment'
  },
  national_weather_service_predictions: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'National Weather Service Predictions',
    branch: 'N/A',
    topic: 'Environment'
  },
  federal_election_commission: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Doc',
    feedName: 'Federal Election Commission',
    branch: 'N/A',
    topic: 'Elections'
  },
  election_assistance_commission: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Doc',
    feedName: 'Election Assistance Commission',
    branch: 'Legislative',
    topic: 'N/A'
  },
  federal_reserve: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Doc',
    feedName: 'Federal Reserve',
    branch: 'Executive',
    topic: 'Economy'
  },
  sec_updates: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'SEC Press Releases',
    branch: 'Executive',
    topic: 'Economy'
  },
  sec_speeches_statements: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'SEC Speeches and Statements',
    branch: 'Executive',
    topic: 'Economy'
  },
  gdp: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'GDP Updates',
    branch: 'Executive',
    topic: 'Economy'
  },
  inflation_updates: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Inflation Updates',
    branch: 'Executive',
    topic: 'Economy'
  },
  wage_growth: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Wage Growth',
    branch: 'Executive',
    topic: 'Economy'
  },
  council_of_economic_advisers: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Council of Economic Advisers',
    branch: 'Executive',
    topic: 'N/A'
  },
  national_economic_council: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'National Economic Council',
    branch: 'Executive',
    topic: 'N/A'
  },
  immigration: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Doc',
    feedName: 'ICE News',
    branch: 'Executive',
    topic: 'Immigration'
  },

  // Spending tables
  doge_savings: { 
    defaultOrderBy: 'uploaded_on',
    allowedOrderBy: ['uploaded_on', 'annual_lease', 'amount', 'created_at'],
    hasAgencyFilter: false,
    conditionalOrdering: {
      'Contract': 'uploaded_on',
      'Grant': 'uploaded_on', 
      'Real Estate': 'annual_lease'
    },
    buttonName: 'Document',
    feedName: 'DOGE Savings',
    branch: 'N/A',
    topic: 'Spending'
  },
  recent_awards: {
    defaultOrderBy: 'start_date',
    allowedOrderBy: ['start_date', 'created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Recent Awards',
    branch: 'N/A',
    topic: 'Spending'
  },

  // Regulations
  regulations: { 
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at', 'effective_date', 'agency_id'],
    hasAgencyFilter: true,
    buttonName: 'Document',
    feedName: 'Regulations',
    branch: 'N/A',
    topic: 'Regulations'
  },

  // Recents/All
  all_recents: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Recents',
    branch: 'N/A',
    topic: 'N/A'
  },

  // Committee meeting feeds
  committee_on_agriculture_meeting_feed: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'House Agriculture Committee',
    branch: 'Legislative',
    topic: 'N/A'
  },
  committee_on_armed_services_meeting_feed: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'House Armed Services Committee',
    branch: 'Legislative',
    topic: 'N/A'
  },
  committee_on_financial_services_meeting_feed: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'House Financial Services Committee',
    branch: 'Legislative',
    topic: 'N/A'
  },
  committee_on_the_judiciary_meeting_feed: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'House Judiciary Committee',
    branch: 'Legislative',
    topic: 'N/A'
  },
  committee_on_foreign_affairs_meeting_feed: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'House Foreign Affairs Committee',
    branch: 'Legislative',
    topic: 'N/A'
  },
  committee_on_appropriations_meeting_feed: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Doc',
    feedName: 'Appropriations Committee',
    branch: 'Legislative',
    topic: 'N/A'
  },
  committee_on_education_and_the_workforce_meeting_feed: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Education and Workforce Committee',
    branch: 'Legislative',
    topic: 'N/A'
  },
  committee_on_energy_and_commerce_meeting_feed: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Energy and Commerce Committee',
    branch: 'Legislative',
    topic: 'N/A'
  },
  committee_on_ethics_meeting_feed: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Ethics Committee',
    branch: 'Legislative',
    topic: 'N/A'
  },
  committee_on_homeland_security_meeting_feed: {
    defaultOrderBy: 'created_at',
    allowedOrderBy: ['created_at'],
    hasAgencyFilter: false,
    buttonName: 'Document',
    feedName: 'Homeland Security Committee',
    branch: 'Legislative',
    topic: 'N/A'
  },
};

// Default config for tables not explicitly defined
const DEFAULT_CONFIG = {
  defaultOrderBy: 'created_at',
  allowedOrderBy: ['created_at'],
  hasAgencyFilter: false,
  buttonName: 'Document',
  feedName: '',
  branch: '',
  topic: 'N/A'
};

// Memoized config getter to prevent recalculation
const getTableConfig = (table) => {
  const config = TABLE_CONFIGS[table];
  if (config) {
    return {
      ...DEFAULT_CONFIG,
      ...config
    };
  }
  return DEFAULT_CONFIG;
};

// Utility function to validate orderBy parameter
const validateOrderBy = (orderBy, allowedValues = []) => {
  if (!orderBy) return false;
  return allowedValues.length === 0 || allowedValues.includes(orderBy);
};

// Helper function to calculate effective orderBy (moved outside component)
const calculateEffectiveOrderBy = (params, config, currentTable) => {
  const { orderBy: queryOrderBy, filterString: queryFilterString } = params;
  
  // Validate and use query parameter if valid
  if (queryOrderBy && validateOrderBy(queryOrderBy, config.allowedOrderBy)) {
    return queryOrderBy;
  }
  
  // Handle conditional ordering for doge_savings
  if (currentTable === 'doge_savings' && !queryOrderBy && queryFilterString) {
    const conditionalOrder = config.conditionalOrdering?.[queryFilterString];
    if (conditionalOrder && validateOrderBy(conditionalOrder, config.allowedOrderBy)) {
      return conditionalOrder;
    }
  }
  
  // Fallback to table default
  return config.defaultOrderBy || 'created_at';
};

export const useFeedPageParams = (table) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Memoize table config to prevent unnecessary recalculations
  const tableConfig = useMemo(() => getTableConfig(table), [table]);
  
  // Memoize URL search params parsing
  const queryParams = useMemo(() => 
    new URLSearchParams(location.search), 
    [location.search]
  );
  
  // Extract query parameters once, including committee API params
  const urlParams = useMemo(() => ({
    orderBy: queryParams.get('orderBy'),
    filterColumn: queryParams.get('filterColumn'),
    filterString: queryParams.get('filterString'),
    page: parseInt(queryParams.get('page')) || 1,
    limit: parseInt(queryParams.get('limit')) || 20,
    // Committee API specific params
    useCommitteeAPI: queryParams.get('useCommitteeAPI') === 'true',
    committee: queryParams.get('committee'),
    chamber: queryParams.get('chamber')
  }), [queryParams]);

  // State with more descriptive initial values
  const [orderBy, setOrderBy] = useState(() => {
    // Calculate initial orderBy value immediately
    return calculateEffectiveOrderBy(urlParams, tableConfig, table);
  });
  
  const [filterColumn, setFilterColumn] = useState(urlParams.filterColumn || '');
  const [filterString, setFilterString] = useState(urlParams.filterString || '');
  const [page, setPage] = useState(urlParams.page);
  const [limit, setLimit] = useState(urlParams.limit);

  // Track previous values to prevent unnecessary updates
  const prevParams = useRef({
    table,
    search: location.search
  });

  // Optimized function to calculate effective orderBy
  const calculateEffectiveOrderByMemo = useCallback((params, config, currentTable) => {
    return calculateEffectiveOrderBy(params, config, currentTable);
  }, []);

  // Optimized URL parameter updater with batch updates
  const updateUrlParams = useCallback((newParams, options = {}) => {
    const { replace = true, scroll = false, preserveCommitteeParams = true } = options;
    const searchParams = new URLSearchParams(location.search);
    
    // Optionally preserve committee API params
    if (preserveCommitteeParams) {
      const existingCommitteeAPI = searchParams.get('useCommitteeAPI');
      const existingCommittee = searchParams.get('committee');
      const existingChamber = searchParams.get('chamber');
      
      // Store them before clearing
      const committeeParams = {
        useCommitteeAPI: existingCommitteeAPI,
        committee: existingCommittee,
        chamber: existingChamber
      };
      
      // Batch parameter updates
      Object.entries(newParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          searchParams.set(key, String(value));
        } else {
          searchParams.delete(key);
        }
      });
      
      // Restore committee params if they existed
      if (committeeParams.useCommitteeAPI) {
        searchParams.set('useCommitteeAPI', committeeParams.useCommitteeAPI);
      }
      if (committeeParams.committee) {
        searchParams.set('committee', committeeParams.committee);
      }
      if (committeeParams.chamber) {
        searchParams.set('chamber', committeeParams.chamber);
      }
    } else {
      // Batch parameter updates without preserving
      Object.entries(newParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          searchParams.set(key, String(value));
        } else {
          searchParams.delete(key);
        }
      });
    }

    const newUrl = `${location.pathname}?${searchParams.toString()}`;
    
    if (replace) {
      navigate(newUrl, { replace: true });
    } else {
      navigate(newUrl);
    }
    
    if (scroll) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location, navigate]);

  // Optimized setters that update both state and URL
  const setOrderByAndUrl = useCallback((newOrderBy) => {
    if (validateOrderBy(newOrderBy, tableConfig.allowedOrderBy)) {
      setOrderBy(newOrderBy);
      updateUrlParams({ orderBy: newOrderBy, page: 1 }); // Reset page when ordering changes
    }
  }, [tableConfig.allowedOrderBy, updateUrlParams]);

  const setFilterAndUrl = useCallback((column, string) => {
    setFilterColumn(column);
    setFilterString(string);
    updateUrlParams({ 
      filterColumn: column, 
      filterString: string, 
      page: 1 // Reset page when filtering changes
    });
  }, [updateUrlParams]);

  const setPageAndUrl = useCallback((newPage) => {
    setPage(newPage);
    updateUrlParams({ page: newPage });
  }, [updateUrlParams]);

  const clearFiltersAndUrl = useCallback(() => {
    setFilterColumn('');
    setFilterString('');
    setPage(1);
    updateUrlParams({ 
      filterColumn: null, 
      filterString: null, 
      page: 1,
      // Also clear committee params
      useCommitteeAPI: null,
      committee: null,
      chamber: null
    }, { preserveCommitteeParams: false });
  }, [updateUrlParams]);

  // Optimized effect that only runs when necessary
  useEffect(() => {
    const currentParams = {
      table,
      search: location.search
    };

    // Only update if table or search params actually changed
    if (
      prevParams.current.table !== currentParams.table ||
      prevParams.current.search !== currentParams.search
    ) {
      const newOrderBy = calculateEffectiveOrderBy(urlParams, tableConfig, table);
      
      // Only update state if values actually changed
      if (newOrderBy !== orderBy) {
        setOrderBy(newOrderBy);
      }
      
      if (urlParams.filterColumn !== filterColumn) {
        setFilterColumn(urlParams.filterColumn || '');
      }
      
      if (urlParams.filterString !== filterString) {
        setFilterString(urlParams.filterString || '');
      }
      
      if (urlParams.page !== page) {
        setPage(urlParams.page);
      }
      
      if (urlParams.limit !== limit) {
        setLimit(urlParams.limit);
      }

      prevParams.current = currentParams;
    }
  }, [table, location.search, urlParams, tableConfig, orderBy, filterColumn, filterString, page, limit]);

  // Memoized return object to prevent unnecessary re-renders
  return useMemo(() => ({
    // Current values
    orderBy,
    filterColumn,
    filterString,
    page,
    limit,
    tableConfig,
    
    // URL parameters (useful for debugging or external integrations)
    urlParams,
    
    // Committee API specific
    isCommitteeAPI: urlParams.useCommitteeAPI,
    committeeName: urlParams.committee,
    chamber: urlParams.chamber,
    
    // Setters that update both state and URL
    setOrderBy: setOrderByAndUrl,
    setFilter: setFilterAndUrl,
    setPage: setPageAndUrl,
    setLimit: (newLimit) => {
      setLimit(newLimit);
      updateUrlParams({ limit: newLimit, page: 1 });
    },
    
    // Utility functions
    updateUrlParams,
    clearFilters: clearFiltersAndUrl,
    
    // Validation helpers
    isValidOrderBy: (value) => validateOrderBy(value, tableConfig.allowedOrderBy),
    
    // Computed values
    hasActiveFilters: !!(filterColumn && filterString) || urlParams.useCommitteeAPI,
    isDefaultOrder: orderBy === (tableConfig.defaultOrderBy || 'created_at'),
    usesCommitteeAPI: tableConfig.usesCommitteeAPI || false,
    
    // Debug info (remove in production)
    _debug: {
      allowedOrderBy: tableConfig.allowedOrderBy,
      currentUrl: location.search,
      parsedParams: urlParams
    }
  }), [
    orderBy, filterColumn, filterString, page, limit, tableConfig, urlParams,
    setOrderByAndUrl, setFilterAndUrl, setPageAndUrl, updateUrlParams, 
    clearFiltersAndUrl, location.search
  ]);
};

// Export table configs for external use if needed
export { TABLE_CONFIGS };

// Export utility functions for testing
export { validateOrderBy, getTableConfig };